import type { Controls } from '../core/device';
import { drawIcon } from '../icons/canvas';
import type { IconId } from '../icons/index';
import type { Random } from '../core/Random';
import { banner, roundRect } from './draw';
import type { MiniGame, MiniInput } from './types';

/** A card: rank 2..14 (ace high), suit 0..3. */
export interface Card {
  rank: number;
  suit: number;
}

export type HandName =
  | 'Royal Flush'
  | 'Straight Flush'
  | 'Four of a Kind'
  | 'Full House'
  | 'Flush'
  | 'Straight'
  | 'Three of a Kind'
  | 'Two Pair'
  | 'Jacks or Better'
  | 'Nothing';

/** Jacks or Better paytable (times your bet), "9/6" like a real bar machine. */
export const PAYTABLE: Record<HandName, number> = {
  'Royal Flush': 250,
  'Straight Flush': 50,
  'Four of a Kind': 25,
  'Full House': 9,
  Flush: 6,
  Straight: 4,
  'Three of a Kind': 3,
  'Two Pair': 2,
  'Jacks or Better': 1,
  Nothing: 0,
};

export function evaluateHand(cards: readonly Card[]): HandName {
  const ranks = cards.map((c) => c.rank).sort((a, b) => a - b);
  const counts = new Map<number, number>();
  for (const r of ranks) counts.set(r, (counts.get(r) ?? 0) + 1);
  const groups = [...counts.values()].sort((a, b) => b - a);
  const flush = cards.every((c) => c.suit === cards[0]?.suit);
  const unique = [...new Set(ranks)];
  const wheel = unique.join() === '2,3,4,5,14';
  const straight = unique.length === 5 && (ranks[4]! - ranks[0]! === 4 || wheel);
  if (straight && flush) return ranks[0] === 10 ? 'Royal Flush' : 'Straight Flush';
  if (groups[0] === 4) return 'Four of a Kind';
  if (groups[0] === 3 && groups[1] === 2) return 'Full House';
  if (flush) return 'Flush';
  if (straight) return 'Straight';
  if (groups[0] === 3) return 'Three of a Kind';
  if (groups[0] === 2 && groups[1] === 2) return 'Two Pair';
  if (groups[0] === 2) {
    const pair = [...counts].find(([, n]) => n === 2)?.[0] ?? 0;
    if (pair >= 11) return 'Jacks or Better';
  }
  return 'Nothing';
}

export function newDeck(rng: Random): Card[] {
  const deck: Card[] = [];
  for (let suit = 0; suit < 4; suit++) for (let rank = 2; rank <= 14; rank++) deck.push({ rank, suit });
  return rng.shuffle(deck);
}

const SUITS = ['suit-spade', 'suit-heart', 'suit-diamond', 'suit-club'] as const satisfies readonly IconId[];
const RANKS: Record<number, string> = { 11: 'J', 12: 'Q', 13: 'K', 14: 'A' };
const DIGITS = ['Digit1', 'Digit2', 'Digit3', 'Digit4', 'Digit5'];

/**
 * The video poker machine in the corner of the Rusty Spur. Jacks or Better: hold the
 * cards you like, draw the rest, and get paid by the table.
 */
export class VideoPokerGame implements MiniGame {
  readonly title = 'Video Poker: Jacks or Better';
  readonly width = 360;
  readonly height = 240;
  hand: Card[];
  held = [false, false, false, false, false];
  phase: 'hold' | 'done' = 'hold';
  result: HandName = 'Nothing';
  /** The card the arrow keys (or a controller's D-pad) point at; shown once they're used. */
  cursor = 0;
  private showCursor = false;
  private readonly deck: Card[];
  private t = 0;
  private overT = -1;

  constructor(
    rng: Random,
    readonly bet: number,
  ) {
    this.deck = newDeck(rng);
    this.hand = this.deck.splice(0, 5);
  }

  get payout(): number {
    return this.phase === 'done' ? PAYTABLE[this.result] * this.bet : 0;
  }

  get done(): boolean {
    return this.phase === 'done' && this.t - this.overT > 2.2;
  }

  hint(controls: Controls): string {
    return {
      keys: 'Press 1-5, click a card, or pick one with ←/→ and ↑ to HOLD. Space/E draws. Jacks or better pays.',
      touch: 'Tap cards to HOLD them, then tap DEAL to draw. Jacks or better pays.',
      pad: 'Pick a card with ←/→ and press ↑ to HOLD it. A draws. Jacks or better pays.',
    }[controls];
  }

  toggle(i: number): void {
    if (this.phase === 'hold') this.held[i] = !this.held[i];
  }

  draw5(): void {
    if (this.phase !== 'hold') return;
    this.hand = this.hand.map((c, i) => (this.held[i] ? c : (this.deck.shift() as Card)));
    this.result = evaluateHand(this.hand);
    this.phase = 'done';
    this.overT = this.t;
  }

  update(dt: number, input: MiniInput): void {
    this.t += dt;
    if (this.phase !== 'hold') return;
    input.keys.forEach((k) => {
      const i = DIGITS.indexOf(k);
      if (i >= 0) this.toggle(i);
      if (k === 'ArrowLeft' || k === 'ArrowRight') {
        if (this.showCursor) this.cursor = (this.cursor + (k === 'ArrowLeft' ? 4 : 1)) % 5;
        this.showCursor = true;
      }
      if (k === 'ArrowUp' || k === 'ArrowDown') {
        if (this.showCursor) this.toggle(this.cursor);
        this.showCursor = true;
      }
    });
    if (input.tap) {
      const { x, y } = input.tap;
      if (y >= 50 && y <= 160) {
        const i = Math.floor((x - 18) / 66);
        if (i >= 0 && i < 5) this.toggle(i);
      } else if (y > 176 && x > 130 && x < 230) this.draw5();
    } else if (input.pressed) this.draw5();
  }

  draw(g: CanvasRenderingContext2D, time: number): void {
    const W = this.width;
    g.fillStyle = '#0b2a5c';
    g.fillRect(0, 0, W, this.height);
    g.fillStyle = '#ffd400';
    g.font = '12px Anton, Impact, sans-serif';
    g.textAlign = 'left';
    g.fillText(`BET $${this.bet}`, 14, 22);
    g.textAlign = 'right';
    g.fillText('ROYAL 250x · 4-KIND 25x · FULL HOUSE 9x · FLUSH 6x', W - 12, 22);
    this.hand.forEach((c, i) => {
      const x = 18 + i * 66;
      const y = 56;
      g.fillStyle = '#ffffff';
      roundRect(g, x, y, 58, 92, 7);
      g.fill();
      const red = c.suit === 1 || c.suit === 2;
      g.fillStyle = red ? '#c62828' : '#111111';
      g.font = '26px Anton, Impact, sans-serif';
      g.textAlign = 'center';
      g.fillText(RANKS[c.rank] ?? String(c.rank), x + 29, y + 38);
      drawIcon(g, SUITS[c.suit] ?? 'suit-spade', x + 15, y + 50, 28);
      if (this.showCursor && this.phase === 'hold' && i === this.cursor) {
        g.strokeStyle = '#ffd400';
        g.lineWidth = 4;
        roundRect(g, x - 4, y - 4, 66, 100, 9);
        g.stroke();
      }
      if (this.held[i]) {
        g.fillStyle = '#ffd400';
        g.font = '14px Anton, Impact, sans-serif';
        g.fillText('HELD', x + 29, y + 110);
      }
    });
    g.fillStyle = this.phase === 'hold' ? '#c62828' : '#555';
    roundRect(g, 130, 180, 100, 34, 8);
    g.fill();
    g.fillStyle = '#ffffff';
    g.font = '18px Bangers, Impact, sans-serif';
    g.textAlign = 'center';
    g.fillText('DEAL', 180, 203);
    if (this.phase === 'done')
      banner(
        g,
        this.payout > 0 ? `${this.result.toUpperCase()}! +$${this.payout}` : 'NOTHING. FEED ME.',
        W / 2,
        44,
        this.payout > 0 ? '#9dff6a' : '#ff8a8a',
        26,
      );
    void time;
  }
}
